import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUpdateCustomer } from "@/domains/customer/customers/hooks/use-customer-mutation";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useMarketingConsent } from "./use-marketing-consent";

// Form validation schema
const customerEditSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      "Please enter a valid email address"
    ),
  phones: z
    .array(
      z.object({
        id: z.string().optional(),
        phone: z.string(),
        label: z.string(),
        isPrimary: z.boolean(),
        clientKey: z.string(),
      })
    )
    .optional(),
  dateOfBirth: z.string().optional(),
  notes: z.string().optional(),
  marketingConsent: z.boolean(),
});

export type CustomerEditFormData = z.infer<typeof customerEditSchema>;

interface UseCustomerEditOptions {
  customer: CustomerDetailFragment;
}

export const useCustomerEdit = ({ customer }: UseCustomerEditOptions) => {
  const { currentSalon } = useSalonStore();
  const { execute: updateCustomer, isLoading, error } = useUpdateCustomer();

  // Marketing consent management
  const {
    hasBothMarketingConsents,
    loading: consentLoading,
    updateBothMarketingConsents,
    isUpdating: isUpdatingConsent,
  } = useMarketingConsent({ customerId: customer.id });

  // Transform customer data for form default values
  const defaultValues = useMemo<CustomerEditFormData>(
    () => ({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      email: customer.email || "",
      phones:
        customer.phoneNumbers
          ?.filter((phone) => phone.isActive)
          .map((phone) => ({
            id: phone.id,
            phone: phone.phone,
            label: phone.label || "mobile",
            isPrimary: phone.isPrimary,
            clientKey: phone.id,
          })) || [],
      dateOfBirth: customer.dateOfBirth
        ? DateTime.fromISO(customer.dateOfBirth).toFormat("yyyy-MM-dd")
        : "",
      notes: customer.notes || "",
      marketingConsent: hasBothMarketingConsents,
    }),
    [
      customer.firstName,
      customer.lastName,
      customer.email,
      customer.dateOfBirth,
      customer.notes,
      customer.phoneNumbers,
      hasBothMarketingConsents,
    ]
  );

  const form = useForm<CustomerEditFormData>({
    resolver: zodResolver(customerEditSchema),
    defaultValues,
    mode: "onChange",
  });

  // Keep defaults in sync (e.g. after async consent loads), but never clobber in-progress edits.
  useEffect(() => {
    if (form.formState.isDirty) return;
    form.reset(defaultValues);
  }, [form, defaultValues, form.formState.isDirty]);

  const handleSubmit = async (data: CustomerEditFormData): Promise<boolean> => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    try {
      const normalizedPhones = (data.phones ?? [])
        .map((phone) => ({
          id: phone.id,
          phone: phone.phone.trim(),
          label: phone.label.trim(),
          isPrimary: phone.isPrimary,
        }))
        .filter((phone) => phone.phone.length > 0);

      // Transform form data for GraphQL mutation
      const updateInput = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || null,
        phones: normalizedPhones,
        dateOfBirth: data.dateOfBirth ? DateTime.fromISO(data.dateOfBirth).toISO() : null,
        notes: data.notes || null,
      };

      // Update customer data
      await updateCustomer(currentSalon.id, customer.id, updateInput);

      // Handle marketing consent changes
      if (data.marketingConsent !== hasBothMarketingConsents) {
        await updateBothMarketingConsents(data.marketingConsent);
      }
      return true;
    } catch (error) {
      console.error("Failed to update customer:", error);
      return false;
    }
  };

  return {
    form,
    handleSubmit,
    isLoading: isLoading || isUpdatingConsent,
    isLoadingConsent: consentLoading,
    error,
  };
};
